import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SealingElementsService } from 'src/app/services/sealing-elements.service';

import { Store } from '@ngrx/store';
import { catchError, filter, map, merge, of, switchMap, tap, withLatestFrom } from 'rxjs';
import * as fromSealingElements from '../fromSealingElement.barrel';

@Injectable()
export class SealingElementsEffects {
  constructor(
    private actions$: Actions,
    private sealingElementsService: SealingElementsService,
    private store: Store<fromSealingElements.State>
  ) { }

  loadSealingElements$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadSealingElements),
    withLatestFrom(
      this.store.select(fromSealingElements.selectUIFilterQueryString),
      this.store.select(fromSealingElements.selectActiveFilterHasValue)
    ),
    filter(([action, activeFilter, hasFilter]) => !!hasFilter),
    switchMap(([action, fil, hasFilter]) => this.sealingElementsService.get(fil)
      .pipe(map(data => fromSealingElements.loadSealingElementsSuccess({ data })),
        catchError(error => of(fromSealingElements.loadSealingElementsFail({ error })))))
  ));

  loadSealingElementsRemoveLoaderIfEmptyFilter$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadSealingElements),
    withLatestFrom(this.store.select(fromSealingElements.selectActiveFilterHasValue)),
    filter(([action, hasFilter]) => !hasFilter),
    map((action, hasFilter) => fromSealingElements.removeLoader({ loader: fromSealingElements.Loaders.loadSealingElements }))
  ));

  /// causes the app to load the results when the filter changes
  // we may want to let the user toggle the take (pageSize) at some point
  onUpdateSealingElementFilter$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.updateSealingElementFilter, fromSealingElements.toggleSellableCode),
    map(action => fromSealingElements.loadSealingElements({ skip: 0, take: 50 }))
  ));

  resetPartNumberFilterOnEquipmentTypeChange$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.updateSealingElementFilter),
    withLatestFrom(this.store.select(fromSealingElements.selectActiveFilter)),
    filter(([action, currFilter]) => !!currFilter.partNumber && ((!action.obj.pluggingHeadPartNumber || action.obj.pluggingHeadPartNumber === '-1'))),
    map(action => fromSealingElements.updateSealingElementFilter({ obj: { partNumber: undefined, pluggingHeadPartNumber: undefined } }))
  ));

  /// Add all the lookups that you want to dispatch at once here... ↓↓↓↓
  loadLookups$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadSealingElementsLookups),
    switchMap(action => merge(
      of(fromSealingElements.loadPipeSizes()),
      of(fromSealingElements.loadMaterials()),
      of(fromSealingElements.loadProductFamilies()),
      of(fromSealingElements.loadSBUs()),
      of(fromSealingElements.loadSubSBUs()),
      of(fromSealingElements.loadSellables()),
      of(fromSealingElements.loadEquipmentTypes()),
      of(fromSealingElements.loadAnsiPipeSizes()),
      of(fromSealingElements.loadPluggingHeads())
    ))));

  loadPluggingHeads$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadPluggingHeads),
    switchMap(action => this.sealingElementsService.getPluggingHeads()
      .pipe(map(res => fromSealingElements.loadPluggingHeadsSuccess({ objs: res })),
        catchError(error => of(fromSealingElements.loadPluggingHeadsFail({ error })))))));

  loadPipeSizes$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadPipeSizes),
    switchMap(action => this.sealingElementsService.getPipeSizesForFilter()
      .pipe(map(data => fromSealingElements.loadPipeSizesSuccess({ data })),
        catchError(error => of(fromSealingElements.loadPipeSizesFail({ error })))))
  ));

  loadMaterials$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadMaterials),
    switchMap(action => this.sealingElementsService.getMaterialsForFilter()
      .pipe(
        map(res => fromSealingElements.loadMaterialsSuccess({ materials: res })),
        catchError(error => of(fromSealingElements.loadMaterialsFail({ error })))))
  ));

  loadProductFamilies$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadProductFamilies),
    switchMap(action => this.sealingElementsService.getProductFamiliesForFilter()
      .pipe(map(res => fromSealingElements.loadProductFamiliesSuccess({ families: res })),
        catchError(error => of(fromSealingElements.loadProductFamiliesFail({ error })))))
  ));

  loadSbus$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadSBUs),
    switchMap(action => this.sealingElementsService.getSbusForFilter()
      .pipe(map(res => fromSealingElements.loadSBUsSuccess({ sbus: res })),
        catchError(error => of(fromSealingElements.loadSubSBUsFail({ error })))))
  ));

  loadSubSbus$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadSubSBUs),
    switchMap(action => this.sealingElementsService.getSubSBUsForFilter()
      .pipe(map(res => fromSealingElements.loadSubSBUsSuccess({ subSBUs: res })),
        catchError(error => of(fromSealingElements.loadSubSBUsFail({ error })))))
  ));

  loadSellables$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadSellables),
    switchMap(action => this.sealingElementsService.getSellablesForFilter()
      .pipe(map(res => fromSealingElements.loadSellablesSuccess({ sellables: res })),
        catchError(error => of(fromSealingElements.loadSellablesFail({ error })))))
  ));

  loadEquipmentTypes$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadEquipmentTypes),
    switchMap(action => this.sealingElementsService.getEquipmentTypesForFilter()
      .pipe(map(equipmentTypes => fromSealingElements.loadEquipmentTypesSuccess({ equipmentTypes })),
        catchError(error => of(fromSealingElements.loadEquipmentTypesFail({ error })))))));

  loadAnsiPipeSizes$ = createEffect(() => this.actions$.pipe(
    ofType(fromSealingElements.loadAnsiPipeSizes),
    switchMap(action => this.sealingElementsService.getANSIPipeSizes()
      .pipe(map(pipes => fromSealingElements.loadAnsiPipeSizesSuccess({ data: pipes })),
        catchError(error => of(fromSealingElements.loadAnsiPipeSizesFail({ error })))))));
}
